import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, Cell, PieChart, Pie, Legend,
} from "recharts";
import { useTheme } from "@/context/ThemeContext";
import { chartTheme, fmtMoney, fmtPct, fmtMonth, fmtNum } from "@/lib/format";

function Tip({ active, payload, label, formatter }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-popover px-3 py-2 text-xs shadow-lg">
      <div className="mb-1 font-medium text-muted-foreground">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 tabular-nums text-popover-foreground">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color || p.fill }} />
          {formatter ? formatter(p) : `${p.name}: ${p.value}`}
        </div>
      ))}
    </div>
  );
}

export function EquityCurveChart({ data, height = 200, currency = "USD" }) {
  const { theme } = useTheme();
  const t = chartTheme(theme === "dark");
  const points = (data?.equity_curve || []).map((p) => ({ ...p, dateLabel: p.date.slice(2) }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={points} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="equityFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={t.areaFill} stopOpacity={0.28} />
            <stop offset="100%" stopColor={t.areaFill} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke={t.grid} strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="dateLabel" tick={{ fontSize: 10, fill: t.axis }} tickLine={false} axisLine={false} minTickGap={48} />
        <YAxis tick={{ fontSize: 10, fill: t.axis }} tickLine={false} axisLine={false} width={52}
          tickFormatter={(v) => fmtMoney(v, currency, true)} domain={["auto", "auto"]} />
        <Tooltip content={<Tip formatter={(p) => `Balance: ${fmtMoney(p.value, currency)}`} />} />
        <Area type="monotone" dataKey="balance" stroke={t.area} strokeWidth={2} fill="url(#equityFill)" dot={false} name="Balance" />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function MonthlyPerfChart({ data, height = 200 }) {
  const { theme } = useTheme();
  const t = chartTheme(theme === "dark");
  const rows = (data?.monthly || []).map((m) => ({ ...m, label: fmtMonth(m.month) }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={rows} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={t.grid} strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 10, fill: t.axis }} tickLine={false} axisLine={false} minTickGap={20} />
        <YAxis tick={{ fontSize: 10, fill: t.axis }} tickLine={false} axisLine={false} width={40} tickFormatter={(v) => `${v}%`} />
        <Tooltip content={<Tip formatter={(p) => `Gain: ${fmtPct(p.payload.gain_pct, true)} (${fmtMoney(p.payload.profit)})`} />} cursor={{ fill: t.grid, opacity: 0.35 }} />
        <Bar dataKey="gain_pct" radius={[4, 4, 0, 0]} name="Monthly gain">
          {rows.map((m, i) => (
            <Cell key={i} fill={m.gain_pct >= 0 ? t.profit : t.loss} fillOpacity={0.85} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export function DrawdownChart({ data, height = 200 }) {
  const { theme } = useTheme();
  const t = chartTheme(theme === "dark");
  const points = (data?.drawdown_curve || []).map((p) => ({ ...p, dateLabel: p.date.slice(2) }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={points} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="ddFill" x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" stopColor={t.loss} stopOpacity={0.3} />
            <stop offset="100%" stopColor={t.loss} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke={t.grid} strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="dateLabel" tick={{ fontSize: 10, fill: t.axis }} tickLine={false} axisLine={false} minTickGap={48} />
        <YAxis tick={{ fontSize: 10, fill: t.axis }} tickLine={false} axisLine={false} width={40} tickFormatter={(v) => `${v}%`} />
        <Tooltip content={<Tip formatter={(p) => `Drawdown: ${fmtPct(p.value)}`} />} />
        <Area type="monotone" dataKey="drawdown" stroke={t.loss} strokeWidth={1.6} fill="url(#ddFill)" dot={false} name="Drawdown" />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function SymbolDonut({ data, height = 200, withLegend = true }) {
  const { theme } = useTheme();
  const t = chartTheme(theme === "dark");
  const rows = (data?.symbols || []).slice(0, 6).map((s) => ({ name: s.symbol, value: Math.abs(s.profit), profit: s.profit }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie data={rows} dataKey="value" nameKey="name" innerRadius="58%" outerRadius="85%" paddingAngle={3} strokeWidth={0}>
          {rows.map((r, i) => (
            <Cell key={i} fill={t.series[i % t.series.length]} />
          ))}
        </Pie>
        {withLegend && <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />}
        <Tooltip content={<Tip formatter={(p) => `${p.name}: ${fmtMoney(p.payload.profit)}`} />} />
      </PieChart>
    </ResponsiveContainer>
  );
}

export function DistributionChart({ data, height = 200 }) {
  const { theme } = useTheme();
  const t = chartTheme(theme === "dark");
  const rows = data?.distribution || [];
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={rows} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={t.grid} strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 9, fill: t.axis }} tickLine={false} axisLine={false} minTickGap={12} />
        <YAxis tick={{ fontSize: 10, fill: t.axis }} tickLine={false} axisLine={false} width={34} allowDecimals={false} />
        <Tooltip content={<Tip formatter={(p) => `${fmtNum(p.value)} trades near ${fmtMoney(p.payload.bucket)}`} />} cursor={{ fill: t.grid, opacity: 0.35 }} />
        <Bar dataKey="count" radius={[3, 3, 0, 0]} name="Trades">
          {rows.map((r, i) => (
            <Cell key={i} fill={r.bucket >= 0 ? t.profit : t.loss} fillOpacity={0.8} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export function TradingHoursChart({ data, height = 200, mode = "hours" }) {
  const { theme } = useTheme();
  const t = chartTheme(theme === "dark");
  const rows = mode === "hours"
    ? (data?.hours || []).map((h) => ({ label: `${h.hour}h`, ...h }))
    : (data?.weekdays || []).map((d) => ({ label: d.day, ...d }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={rows} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={t.grid} strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 9, fill: t.axis }} tickLine={false} axisLine={false} interval={mode === "hours" ? 2 : 0} />
        <YAxis tick={{ fontSize: 10, fill: t.axis }} tickLine={false} axisLine={false} width={44} tickFormatter={(v) => fmtMoney(v, "USD", true)} />
        <Tooltip content={<Tip formatter={(p) => `Profit: ${fmtMoney(p.value)} (${fmtNum(p.payload.trades)} trades)`} />} cursor={{ fill: t.grid, opacity: 0.35 }} />
        <Bar dataKey="profit" radius={[3, 3, 0, 0]} name="Profit">
          {rows.map((r, i) => (
            <Cell key={i} fill={r.profit >= 0 ? t.profit : t.loss} fillOpacity={0.8} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export function ChartWidget({ type, data, compact }) {
  const height = compact ? 120 : "100%";
  const wrap = (chart) => <div className="h-full min-h-[110px]">{chart}</div>;
  const cur = data?.account?.currency || "USD";
  switch (type) {
    case "equity_curve":
      return wrap(<EquityCurveChart data={data} height="100%" currency={cur} />);
    case "monthly_performance":
      return wrap(<MonthlyPerfChart data={data} height="100%" />);
    case "drawdown_chart":
      return wrap(<DrawdownChart data={data} height="100%" />);
    case "symbol_breakdown":
      return wrap(<SymbolDonut data={data} height="100%" withLegend={!compact} />);
    case "win_loss_distribution":
      return wrap(<DistributionChart data={data} height="100%" />);
    case "trading_hours":
      return wrap(<TradingHoursChart data={data} height="100%" />);
    default:
      return null;
  }
}
